import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ChatPanel } from './ChatPanel';
import { 
  LayoutDashboard, 
  Trophy, 
  MessageSquare, 
  BarChart3, 
  HelpCircle,
  LogOut,
  Menu,
  X,
  Bot,
  Building2
} from 'lucide-react';
import { Button } from './ui/button';

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/clients', icon: Building2, label: 'Clients' },
  { path: '/tournaments', icon: Trophy, label: 'Tournaments' },
  { path: '/support', icon: HelpCircle, label: 'Support' },
];

const adminItems = [
  { path: '/analytics', icon: BarChart3, label: 'Analytics' },
];

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const allNavItems = user?.role === 'admin' 
    ? [...navItems, ...adminItems] 
    : navItems;

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 sidebar transform transition-transform duration-200
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full p-4">
          {/* Logo */}
          <div className="flex items-center gap-3 px-2 py-4 mb-6">
            <div className="w-10 h-10 rounded-lg overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_tournament-finder-2/artifacts/bgo2ljk1_XO%20Logo%282200x2200%29.png"
                alt="XO"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h1 className="font-outfit font-bold text-lg text-white">XOClaw</h1>
              <p className="text-xs text-[#A1A1AA]">AI Agent by XO</p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1">
            {allNavItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-3 py-2.5 rounded-md
                    transition-all duration-200
                    ${isActive 
                      ? 'bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30' 
                      : 'text-[#A1A1AA] hover:bg-[#1A1A1E] hover:text-white'
                    }
                  `}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="border-t border-[#27272A] pt-4 mt-4">
            <div className="flex items-center gap-3 px-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-[#1A1A1E] flex items-center justify-center">
                <span className="text-sm font-medium text-[#00F0FF]">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-[#A1A1AA] truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-[#A1A1AA] hover:text-[#FF0055] hover:bg-[#FF0055]/10"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="sticky top-0 z-30 glass border-b border-white/10 px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              className="lg:hidden p-2 text-white"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </button>
            
            <div className="flex-1" />
            
            <Button
              onClick={() => setChatOpen(!chatOpen)}
              className="bg-[#00F0FF] text-black hover:bg-[#33F3FF] font-bold"
              data-testid="ai-chat-toggle"
            >
              <Bot className="w-5 h-5 mr-2" />
              AI Assistant
            </Button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6">
          {children}
        </main>
      </div>

      {/* Chat Panel */}
      <ChatPanel isOpen={chatOpen} onClose={() => setChatOpen(false)} />
    </div>
  );
}
